import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import {
  MAX_FILE_SIZE_MB,
  SUPPORTED_FILE_EXTENSION_TO_MIME,
  SUPPORTED_FILE_TYPES,
} from '../../contracts';
import type { LibraryUploadFile } from './upload.types';
import { uploadDocumentModalMessages } from './uploadDocumentModal.messages';
import { uploadValidationMessages } from './uploadValidation.messages';

const validateUploadFile = (
  file: File,
  intl: ReturnType<typeof useIntl>,
  messages: { unsupportedType: string; tooLarge: string }
) => {
  const extension = file.name.split('.').pop()?.toLowerCase();

  if (file.type === 'application/msword' || extension === 'doc') {
    return {
      valid: false,
      error: intl.formatMessage(uploadValidationMessages.legacyDocUnsupported),
    };
  }

  const declaredFileConfig = file.type
    ? SUPPORTED_FILE_TYPES[file.type as keyof typeof SUPPORTED_FILE_TYPES]
    : undefined;
  const fallbackMimeType = extension
    ? SUPPORTED_FILE_EXTENSION_TO_MIME[extension]
    : undefined;
  const fileConfig =
    declaredFileConfig ??
    (fallbackMimeType ? SUPPORTED_FILE_TYPES[fallbackMimeType] : undefined);

  if (!fileConfig) return { valid: false, error: messages.unsupportedType };
  if (file.size > fileConfig.maxSize) return { valid: false, error: messages.tooLarge };
  return { valid: true, error: null };
};

export const useFileValidation = () => {
  const intl = useIntl();

  return useCallback(
    (file: LibraryUploadFile) =>
      validateUploadFile(file, intl, {
        unsupportedType: intl.formatMessage(
          uploadDocumentModalMessages.unsupportedFileType
        ),
        tooLarge: intl.formatMessage(uploadDocumentModalMessages.fileTooLarge, {
          size: MAX_FILE_SIZE_MB,
        }),
      }),
    [intl]
  );
};
